import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface ProfitabilitySnapshotByVehicle {
  /** Revenue for the period (sum over orders) */
  revenue: number;
  /** Total cost for the period: order cost + fuel cost + driver cost */
  totalCost: number;
}

export interface ProfitabilitySnapshot {
  period: { startDate: string; endDate: string };
  /** Per-vehicle totals; keys are vehicle IDs (e.g. reg numbers) */
  byVehicle: Record<string, ProfitabilitySnapshotByVehicle>;
  /** Vehicle IDs that appear only in Jigsaw data (excluded from Unmapped row in Cost Manage) */
  jigsawOnlyVehicleIds?: string[];
  updatedAt: number;
}

interface ProfitabilitySnapshotContextType {
  snapshot: ProfitabilitySnapshot | null;
  setSnapshot: (snapshot: ProfitabilitySnapshot | null) => void;
  clearSnapshot: () => void;
}

const ProfitabilitySnapshotContext = createContext<ProfitabilitySnapshotContextType | undefined>(undefined);

export function ProfitabilitySnapshotProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshotState] = useState<ProfitabilitySnapshot | null>(null);
  const setSnapshot = useCallback((value: ProfitabilitySnapshot | null) => {
    setSnapshotState(value);
  }, []);
  const clearSnapshot = useCallback(() => setSnapshotState(null), []);
  return (
    <ProfitabilitySnapshotContext.Provider value={{ snapshot, setSnapshot, clearSnapshot }}>
      {children}
    </ProfitabilitySnapshotContext.Provider>
  );
}

export function useProfitabilitySnapshot() {
  const context = useContext(ProfitabilitySnapshotContext);
  if (context === undefined) {
    throw new Error('useProfitabilitySnapshot must be used within a ProfitabilitySnapshotProvider');
  }
  return context;
}
